import { useCallback, useEffect, useRef } from 'react'
import {
  generatePath,
  Redirect,
  Route,
  Switch,
  useHistory,
} from 'react-router-dom'
import { readRmsDomains } from '@racepointenergy/swc/scs/read-rms-domains'

import ROUTES from 'app/routes'
import { fetchHomeThunk } from 'features/home'

import { EMPTY_HOME_ID } from '../consts'
import AddNetwork from './AddNetwork'
import ClaimDevice from './ClaimDevice'
import DevicesList from './DevicesList'
import CreateWLANs from './CreateWLANs'
import PurchaseDeviceSubscription from './PurchaseDeviceSubscription'
import SaveTemplate from './CreateWLANs/CreateWLANsSaveTemplate'
import SelectTemplate from './CreateWLANs/CreateWLANsTemplate'
import CreateWLANCopyHome from './CreateWLANs/CreateWLANCopyHome'

import { useNetworkHomeJobsRouteParams } from '../hooks'
import { NetworkWizardContext } from './NetworkWizardContext'

import { useAppDispatch, useQuery } from 'app/hooks'
import CreateWLANsSetup from './CreateWLANs/CreateWLANsSetup'

function NetworkWizard() {
  const history = useHistory()
  const query = useQuery()
  const dispatch = useAppDispatch()

  const referrerHomeId = useRef(
    typeof query.referrerHomeId === 'string' ? query.referrerHomeId : undefined
  )
  const referrerDomainId = useRef(
    typeof query.referrerDomainId === 'string'
      ? query.referrerDomainId
      : undefined
  )
  const { rootDomainId, jobId, homeId } = useNetworkHomeJobsRouteParams()

  const getDefaultRoute = () => {
    if (homeId === EMPTY_HOME_ID) {
      return generatePath(ROUTES.networkAi.wizard.rootNoJobAddNetwork, {
        rootDomainId,
      })
    }

    return generatePath(ROUTES.networkAi.wizard.addNetwork, {
      rootDomainId,
      jobId,
      homeId,
    })
  }

  const {
    job,
    isLoading,
    isError,
    refetch: refetchJobData,
  } = readRmsDomains.useQuery(
    { domainid: jobId, homes: true, jobs: false },
    {
      skip: !jobId,
      selectFromResult: ({ data, isLoading, isError }) => ({
        job: data?.[0], // it returns an array with one job only that has id = jobId
        isLoading,
        isError,
      }),
      refetchOnMountOrArgChange: true,
    }
  )

  const handleCloseWizard = useCallback(() => {
    if (!rootDomainId) {
      return
    }

    if (job) {
      // TODO possibly fix to properly redirect to job in case of "no home"
      const path = generatePath(ROUTES.networkAi.dashboard.root, {
        homeId,
        jobId,
        rootDomainId,
        type: 'home',
      })
      return history.push(path)
    }

    return history.push(ROUTES.homesJobs(rootDomainId))
  }, [rootDomainId, job, history, homeId, jobId])

  useEffect(() => {
    if (isError) {
      console.log('Error fetching home or job data for a NetworkAI Wizard.')
    }
  }, [isError])

  useEffect(() => {
    dispatch(fetchHomeThunk(homeId))
  }, [homeId, dispatch])

  return (
    <NetworkWizardContext.Provider
      value={{
        job,
        homeId: referrerHomeId.current,
        domainId: referrerDomainId.current,
        isLoading,
        handleCloseWizard,
        refetchJobData,
      }}
    >
      <Switch>
        <Route path={ROUTES.networkAi.wizard.rootNoJobAddNetwork}>
          <AddNetwork />
        </Route>
        <Route path={ROUTES.networkAi.wizard.addNetwork}>
          <AddNetwork />
        </Route>
        <Route path={ROUTES.networkAi.wizard.claimDevice}>
          <ClaimDevice />
        </Route>
        <Route path={ROUTES.networkAi.wizard.devicesList}>
          <DevicesList />
        </Route>
        <Route path={ROUTES.networkAi.wizard.createWLANs} exact>
          <CreateWLANs />
        </Route>
        <Route path={ROUTES.networkAi.wizard.createWLANsSetup}>
          <CreateWLANsSetup />
        </Route>
        <Route path={ROUTES.networkAi.wizard.purchaseSubscription}>
          <PurchaseDeviceSubscription />
        </Route>
        <Route path={ROUTES.networkAi.wizard.saveTemplate}>
          <SaveTemplate />
        </Route>
        <Route path={ROUTES.networkAi.wizard.selectTemplate}>
          <SelectTemplate />
        </Route>
        <Route path={ROUTES.networkAi.wizard.copyHome}>
          <CreateWLANCopyHome />
        </Route>
        <Route path="*">
          <Redirect to={getDefaultRoute()} />
        </Route>
      </Switch>
    </NetworkWizardContext.Provider>
  )
}

export default NetworkWizard
